import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import type { Item, TripTypeItem, CreateItemInput, UpdateItemInput, CreateTripTypeItemInput, UpdateTripTypeItemInput, ItemPriority } from '../types'

export const useItems = () => {
  const [items, setItems] = useState<Item[]>([])
  const [tripTypeItems, setTripTypeItems] = useState<TripTypeItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Load items and trip type items from Supabase
  useEffect(() => {
    loadItems()
    loadTripTypeItems()
  }, [])

  const loadItems = async () => {
    try {
      setError(null)
      
      const { data, error } = await supabase
        .from('items')
        .select('*')
        .order('name')

      if (error) throw error

      const mappedItems: Item[] = data.map(row => ({
        id: row.id.toString(),
        name: row.name,
        description: row.description,
        createdAt: new Date(row.created_at),
        updatedAt: new Date(row.updated_at),
      }))

      setItems(mappedItems)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load items')
    }
  }

  const loadTripTypeItems = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const { data, error } = await supabase
        .from('trip_type_items')
        .select('*')
        .order('created_at')

      if (error) throw error

      const mappedTripTypeItems: TripTypeItem[] = data.map(row => ({
        id: row.id.toString(),
        tripTypeId: row.trip_type_id.toString(),
        itemId: row.item_id.toString(),
        priority: row.priority as ItemPriority,
        createdAt: new Date(row.created_at),
      }))

      setTripTypeItems(mappedTripTypeItems)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load trip type items')
    } finally {
      setLoading(false)
    }
  }

  const createItem = async (input: CreateItemInput) => {
    try {
      setError(null)
      
      // Get current user or use temp user
      const { data: { user } } = await supabase.auth.getUser()
      const userId = user?.id || 'temp-user-id'
      
      const { data, error } = await supabase
        .from('items')
        .insert({
          user_id: userId,
          name: input.name,
          description: input.description,
        })
        .select()
        .single()

      if (error) throw error

      const newItem: Item = {
        id: data.id.toString(),
        name: data.name,
        description: data.description,
        createdAt: new Date(data.created_at),
        updatedAt: new Date(data.updated_at),
      }

      setItems(prev => [...prev, newItem])
      return newItem
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create item'
      setError(errorMessage)
      throw new Error(errorMessage)
    }
  }

  const updateItem = async (input: UpdateItemInput) => {
    try {
      setError(null)
      
      const { data, error } = await supabase
        .from('items')
        .update({
          name: input.name,
          description: input.description,
        })
        .eq('id', parseInt(input.id))
        .select()
        .single()

      if (error) throw error

      const updatedItem: Item = {
        id: data.id.toString(),
        name: data.name,
        description: data.description,
        createdAt: new Date(data.created_at),
        updatedAt: new Date(data.updated_at),
      }

      setItems(prev => 
        prev.map(item => 
          item.id === input.id ? updatedItem : item
        )
      )
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update item'
      setError(errorMessage)
      throw new Error(errorMessage)
    }
  }

  const deleteItem = async (id: string) => {
    try {
      setError(null)
      
      const { error } = await supabase
        .from('items')
        .delete()
        .eq('id', parseInt(id))

      if (error) throw error

      setItems(prev => prev.filter(item => item.id !== id))
      // Trip type items will be automatically deleted due to CASCADE
      setTripTypeItems(prev => prev.filter(tti => tti.itemId !== id))
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete item'
      setError(errorMessage)
      throw new Error(errorMessage)
    }
  }

  const addItemToTripType = async (input: CreateTripTypeItemInput) => {
    try {
      setError(null)
      
      const { data, error } = await supabase
        .from('trip_type_items')
        .insert({
          trip_type_id: parseInt(input.tripTypeId),
          item_id: parseInt(input.itemId),
          priority: input.priority,
        })
        .select()
        .single()

      if (error) throw error

      const newTripTypeItem: TripTypeItem = {
        id: data.id.toString(),
        tripTypeId: data.trip_type_id.toString(),
        itemId: data.item_id.toString(),
        priority: data.priority as ItemPriority,
        createdAt: new Date(data.created_at),
      }

      setTripTypeItems(prev => [...prev, newTripTypeItem])
      return newTripTypeItem
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add item to trip type'
      setError(errorMessage)
      throw new Error(errorMessage)
    }
  }

  const updateTripTypeItemPriority = async (input: UpdateTripTypeItemInput) => {
    try {
      setError(null)
      
      const { data, error } = await supabase
        .from('trip_type_items')
        .update({
          priority: input.priority,
        })
        .eq('id', parseInt(input.id))
        .select()
        .single()

      if (error) throw error

      const updatedTripTypeItem: TripTypeItem = {
        id: data.id.toString(),
        tripTypeId: data.trip_type_id.toString(),
        itemId: data.item_id.toString(),
        priority: data.priority as ItemPriority,
        createdAt: new Date(data.created_at),
      }

      setTripTypeItems(prev => 
        prev.map(tti => 
          tti.id === input.id ? updatedTripTypeItem : tti
        )
      )
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update item priority'
      setError(errorMessage)
      throw new Error(errorMessage)
    }
  }

  const removeItemFromTripType = async (tripTypeItemId: string) => {
    try {
      setError(null)
      
      const { error } = await supabase
        .from('trip_type_items')
        .delete()
        .eq('id', parseInt(tripTypeItemId))

      if (error) throw error

      setTripTypeItems(prev => prev.filter(tti => tti.id !== tripTypeItemId))
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to remove item from trip type'
      setError(errorMessage)
      throw new Error(errorMessage)
    }
  }

  const getItemsForTripType = (tripTypeId: string) => {
    const tripTypeItemsForType = tripTypeItems.filter(tti => tti.tripTypeId === tripTypeId)
    return tripTypeItemsForType.map(tti => {
      const item = items.find(item => item.id === tti.itemId)
      return item ? { ...item, priority: tti.priority, tripTypeItemId: tti.id } : null
    }).filter(Boolean) as (Item & { priority: ItemPriority, tripTypeItemId: string })[]
  }

  return {
    items,
    tripTypeItems,
    loading,
    error,
    createItem,
    updateItem,
    deleteItem,
    addItemToTripType,
    updateTripTypeItemPriority,
    removeItemFromTripType,
    getItemsForTripType,
    refetch: () => {
      loadItems()
      loadTripTypeItems()
    },
  }
}